import { useTranslation } from "react-i18next";
import { Integration } from "/app/src/models";
import Box from "/app/src/components/generic/components/box";
import { Formik, FormikProps, FormikHelpers } from "formik";
import { useCallback, useMemo } from "react";
import { Form, Input, Select, SubmitButton } from "formik-antd";
import { simpleSchemaBuilder } from "/app/src/helpers";
import { IconToolTip } from "/app/src/components/icons/IconBuilder";
import { integrationService } from "/app/src/services";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handlePromiseError } from "/app/src/helpers/api";
import GridWrapper from "/app/src/components/generic/gridWrapper";

const ORDER_CREATION_TYPE_MANUAL = "manual";

interface ManualSettingsFormValues {
  name: string;
}

/**
 * Integration settings form component.
 * @param param0 - Props for the component.
 * @returns IntegrationSetting component.
 */
export default function IntegrationSetting({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const creationTypeLabel = useMemo(() => {
    if (integration?.appName === "Order Processor") {
      return (
        <>
          {t("translation:order_processor_type")}
          <IconToolTip
            content={t("translation:order_processor_type_tip")}
            className="ml-1"
          />
        </>
      );
    }

    return (
      <>
        {t("translation:order_creation_type")}
        <IconToolTip
          content={t("translation:order_builder_type_tip")}
          className="ml-1"
        />
      </>
    );
  }, [integration?.appName, t]);

  const { mutateAsync: updateIntegration } = useMutation({
    mutationFn: (updatedIntegration: Integration) => {
      return integrationService
        .updateSingle(integration.id, updatedIntegration)
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["integration", Number(integration.id)],
        response,
      );
    },
  });

  const handleSubmit = useCallback(
    async (
      values: ManualSettingsFormValues,
      actions: FormikHelpers<ManualSettingsFormValues>,
    ) => {
      await updateIntegration({
        id: integration.id,
        name: values.name,
      }).then(() => {
        actions.resetForm();
      });
    },
    [integration, updateIntegration],
  );

  const integrationSettingsForm: (
    props: FormikProps<ManualSettingsFormValues>,
  ) => JSX.Element = useCallback(
    ({ dirty, isSubmitting }) => (
      <Form layout="vertical">
        <div className="flex justify-between items-center">
          <h2 className="text-[18px]">
            {t("translation:integration_settings")}
          </h2>
          <SubmitButton type="primary" disabled={!dirty || isSubmitting}>
            {t("translation:save")}
          </SubmitButton>
        </div>
        <GridWrapper responsive={{ sm: 1, md: 1, lg: 3 }}>
          <Form.Item
            name="name"
            label={t("translation:integration_name")}
            hasFeedback={false}
          >
            <Input
              name="name"
              placeholder={t("translation:enter_name")}
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="number"
            label={creationTypeLabel}
            hasFeedback={false}
          >
            <Select disabled name="number" size="large">
              <Select.Option value="manual">
                {t("translation:manual")}
              </Select.Option>
              <Select.Option value="automatic">
                {t("translation:automatic")}
              </Select.Option>
            </Select>
          </Form.Item>
        </GridWrapper>
      </Form>
    ),
    [creationTypeLabel, t],
  );

  return (
    <Box>
      <Formik
        component={integrationSettingsForm}
        initialValues={{
          name: integration.name || "",
          number: integration.number || ORDER_CREATION_TYPE_MANUAL,
        }}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={simpleSchemaBuilder([
          { name: "name", type: "string", required: true },
        ])}
      />
    </Box>
  );
}
